import { formatAmount } from "@/lib/utils";
import moment from "moment";
import { useEffect, useState } from "react";

const TOPUP_WINDOW_MS = 10 * 60 * 1000;
const REPORT_DELAY_MS = 2 * 60 * 1000;

function formatCountdown(ms: number): string {
  const totalSecs = Math.max(0, Math.floor(ms / 1000));
  const mins = Math.floor(totalSecs / 60);
  const secs = totalSecs % 60;
  return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
}

const TopupCountdown = ({
  createdAt,
  transactionId,
  amount,
}: {
  createdAt: string;
  transactionId: number | string;
  amount: string | number;
}) => {
  const [, tick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const elapsed = Date.now() - new Date(createdAt).getTime();
  const phase1Remaining = TOPUP_WINDOW_MS - elapsed;
  const phase2Remaining = TOPUP_WINDOW_MS + REPORT_DELAY_MS - elapsed;

  const reportText = encodeURIComponent(
    `Hi, I have a pending card top-up that hasn't been credited.\n\nTransaction ID: ${transactionId}\nAmount: $${formatAmount(amount)}\nDate: ${moment(createdAt).format("DD MMM YYYY, hh:mm A")}\n\nPlease check the status.`,
  );
  const telegramUrl = `https://t.me/dynascentglobalofficial?text=${reportText}`;

  if (phase1Remaining > 0) {
    return (
      <div className="bg-dark-card-4 border-dark-stroke-5 mt-3 rounded-xl border px-4 py-4">
        <p className="text-dark-text-300 text-xs font-semibold">
          Estimated credit time
        </p>
        <p className="text-dark-primary-main mt-1 text-4xl font-bold tabular-nums">
          {formatCountdown(phase1Remaining)}
        </p>
        <p className="text-dark-text-50 mt-1 text-sm">
          Your card will be credited within this window.
        </p>
      </div>
    );
  }

  if (phase2Remaining > 0) {
    return (
      <div className="bg-dark-warning-300/10 border-dark-warning-300/30 mt-3 rounded-xl border px-4 py-4">
        <p className="text-dark-warning-300 text-xs font-semibold">
          Processing delay
        </p>
        <p className="text-dark-warning-300 mt-1 text-4xl font-bold tabular-nums">
          {formatCountdown(phase2Remaining)}
        </p>
        <p className="text-dark-text-50 mt-1 text-sm">
          Processing is taking longer than expected. Please wait before
          reporting.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-dark-card-4 border-dark-stroke-5 mt-3 rounded-2xl border px-4 py-4">
      <p className="text-dark-text-300 mb-4 text-sm leading-5">
        Processing is taking longer than expected. If you&apos;ve already made a
        payment, tap below to notify our support team.
      </p>
      <a
        href={telegramUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="bg-dark-error-300 hover:bg-dark-error-300/90 text-dark-text-50 flex w-full items-center justify-center rounded-full py-3.5 text-sm font-bold transition-transform active:scale-95"
      >
        Report Transaction
      </a>
    </div>
  );
};

export default TopupCountdown;
